import { Flex, IconButton, Input, InputLeftElement, InputGroup, Menu, MenuButton, MenuList, MenuItem, Select, Table, Th, Thead, Tr, Tbody, Text } from "@chakra-ui/react";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { AiFillCheckCircle } from "react-icons/ai";
import { ImSearch } from "react-icons/im";
import { RiFilterFill, RiFilterOffFill } from "react-icons/ri";

import ColumnOptionsPropType from "./ColumnOptionsPropType";
import EditableTablePageNavigation from "./EditableTablePageNavigation";
import EditableTableRow from "./EditableTableRow";
import EditRowModal from "./EditRowModal";

function EditableTable(props) {
  const { tableData, columnOptions, rowOnClick, searchFn, searchText, submitHandler, tableName, page, pageSize, setPage, setPageSize, totalPages, totalItems } = props;
  const [colOptions, setColOptions] = useState([...columnOptions]);

  const filteredTableData = tableData.filter((row) => {
    for (let i = 0; i < colOptions.length; i++) {
      const isFilter = colOptions[i].filter;
      const { key, filterValue } = colOptions[i];

      if (isFilter && filterValue && row[key] !== filterValue) {
        return false;
      }
    }
    return true;
  });

  const [modalRowData, setModalRowData] = useState({});
  const [modalRowOnSubmit, setModalRowOnSubmit] = useState(() => () => {});
  const [showModal, setShowModal] = useState(false);

  const allColumnsNotEditable = tableData.every((row) => colOptions.every((option) => !option.editable(row)));
  const numColumns = colOptions.length + !allColumnsNotEditable;

  function splitTableData(data) {
    const paddedArr = Array(pageSize).fill({});

    if (totalPages !== undefined) {
      const result = [];
      result[page] = data.concat(paddedArr).slice(0, pageSize);
      return result;
    }

    const result = [null];
    for (let i = 0; i < data.length; i += pageSize) {
      result.push(
        data
          .slice(i, i + pageSize)
          .concat(paddedArr)
          .slice(0, pageSize),
      );
    }

    if (data.length === 0) {
      result.push(paddedArr);
    }

    return result;
  }

  const pages = splitTableData(filteredTableData);
  const totalNumPages = totalPages ?? Math.max(1, pages.length - 1); // pages array has a null at index 0
  const totalNumItems = totalItems ?? filteredTableData.length;
  const currentNumItemsShown = page < totalNumPages ? pageSize : Math.max(0, totalNumItems - (page - 1) * pageSize);

  const displayEditRowModal = (rowData, onSubmit) => {
    const handleSubmit = async (data) => {
      const res = await onSubmit(data);
      if (res.ok) {
        setShowModal(false);
      }
    };

    setShowModal(true);
    setModalRowData(rowData);
    setModalRowOnSubmit(() => handleSubmit);
  };

  const columns = colOptions.map((option, i) => {
    const isFilter = option.filter;
    const filterValue = option.filterValue;
    const onClick = (oldVal, newVal) => {
      if (oldVal === newVal) {
        option.filterValue = "";
      } else {
        option.filterValue = newVal;
      }
      setColOptions([...colOptions]);
    };

    const filterList = option.values && (
      <MenuList>
        {option.values.map((val, i) => {
          return (
            <MenuItem onClick={() => onClick(option.filterValue, val)} icon={<AiFillCheckCircle visibility={val === option.filterValue ? "" : "hidden"} />} key={i}>
              {val}
            </MenuItem>
          );
        })}
      </MenuList>
    );

    let filterIcon = null;
    if (isFilter && filterValue) {
      filterIcon = <RiFilterFill size={20} />;
    } else if (isFilter && !filterValue) {
      filterIcon = <RiFilterOffFill size={20} />;
    }

    const filterButton = (
      <Menu>
        <MenuButton as={IconButton} icon={filterIcon} />
        {filterList}
      </Menu>
    );

    return (
      <Th color="#007bff" fontWeight="bold" fontSize={16} className="custom-scrollbar" width={option.columnWidth ?? ""} overflowX="auto" textAlign="center" padding="10px 0" key={i}>
        {option.columnName}
        {option.filter && filterButton}
      </Th>
    );
  });

  return (
    <Flex maxW="100%" userSelect="none" flexDirection="column">
      <EditRowModal isOpen={showModal} handleClose={() => setShowModal(false)} onSubmit={modalRowOnSubmit} columnOptions={colOptions} rowData={modalRowData} />
      <Flex justifyContent="space-between">
        <Flex flexDirection="column">
          <Text color="#3483be" align="left" fontWeight="bold" fontSize={28}>
            {tableName || ""}
          </Text>
          {totalNumItems && (
            <Text color="#3483be" align="left" fontSize={16}>
              Showing {currentNumItemsShown} out of {totalNumItems} items
            </Text>
          )}
        </Flex>

        <Flex align="center">
          {setPageSize && (
            <Select
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
              }}
              width="200px"
              mr={searchFn ? "15px" : "0"}
              cursor="pointer"
              size="md"
            >
              <option value={10}>10 Items per page</option>
              <option value={20}>20 Items per page</option>
              <option value={50}>50 Items per page</option>
            </Select>
          )}
          {searchFn && (
            <InputGroup maxW="240px">
              <InputLeftElement pointerEvents="none">
                <ImSearch color="gray.300" />
              </InputLeftElement>
              <Input onChange={searchFn} type="tel" placeholder={searchText} />
            </InputGroup>
          )}
        </Flex>
      </Flex>
      <Table style={{ tableLayout: "fixed" }} width="100%" margin="20px 0">
        <Thead borderBottom="black 2px solid">
          <Tr>
            {columns}
            {!allColumnsNotEditable && <Th w="60px" />}
          </Tr>
        </Thead>
        <Tbody>
          {pages[page]?.map((rowData, i) => (
            <EditableTableRow displayEditRowModal={displayEditRowModal} rowOnClick={rowOnClick} i={i} key={i} rowData={rowData} columnOptions={colOptions} submitHandler={submitHandler} />
          ))}
          <EditableTablePageNavigation
            numColumns={numColumns}
            numPages={totalNumPages}
            pageNum={page}
            setPageNum={(newPageNum) => {
              setPage(newPageNum);
              if (setPage) setPage(newPageNum);
            }}
          />
        </Tbody>
      </Table>
    </Flex>
  );
}

EditableTable.propTypes = {
  tableData: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  columnOptions: ColumnOptionsPropType,
  submitHandler: PropTypes.func,
  tableName: PropTypes.string,
  rowOnClick: PropTypes.func,
  searchFn: PropTypes.func,
  searchText: PropTypes.string,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  setPage: PropTypes.func,
  setPageSize: PropTypes.func,
};

EditableTable.defaultProps = {
  submitHandler: () => {},
  tableData: [],
};

export default EditableTable;
